/**
 * Sentry Error Tracking
 * Initializes Sentry for error monitoring and performance tracking
 */

import * as Sentry from "@sentry/react";

// Declare global __APP_VERSION__ from vite.config.ts
declare const __APP_VERSION__: string;

/**
 * Initialize Sentry error tracking
 * Call this FIRST in main.tsx, before any other code
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip initialization if no DSN configured
  if (!dsn) {
    console.log("[Sentry] No DSN configured, error tracking disabled");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: `vizi-admin@${__APP_VERSION__}`,
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay for debugging errors
      Sentry.replayIntegration({
        // Capture 10% of all sessions
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance monitoring: sample 10% of transactions
    tracesSampleRate: 0.1,
    // Session replay: capture 100% of sessions with errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Filter out development noise
    beforeSend(event) {
      // Don't send events in development unless explicitly enabled
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.log("[Sentry] Skipping event in development:", event);
        return null;
      }
      return event;
    },
  });

  console.log("[Sentry] Initialized for", import.meta.env.MODE);
}

/**
 * Set the current user for Sentry context
 * Call this when user logs in
 */
export function setSentryUser(
  user: { id: string; email?: string } | null,
): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture an exception manually
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
): void {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
): void {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  Sentry.addBreadcrumb(breadcrumb);
}

// Re-export Sentry for advanced usage
export { Sentry };
